import { useQuery } from '@tanstack/react-query';
import { useDebounce } from './useDebounce';

/**
 * useServerSearch — the ONE canonical server-search hook (SEARCH-CERT H3).
 *
 * Replaces the ~14 hand-rolled "debounce → fetch → swap list" copies scattered across
 * pages. Encapsulates every property those copies kept getting wrong individually:
 *  - debounce (default 300ms)
 *  - min-chars gate (default 2) so a single keystroke never hits the server
 *  - React Query keyed by the debounced term → only the LATEST result is used
 *    (no stale-response race) and identical queries dedupe
 *  - `results` is [] until the gate opens, so clearing the box clears the list
 *
 * Usage:
 *   const { isSearching, results, loading } = useServerSearch(search, {
 *     key: ['hearings-search'],
 *     fetcher: (q) => searchHearings(q),
 *   });
 *   const list = isSearching ? results : clientFilteredList;
 */
export interface ServerSearchOptions<T> {
  /** Stable base query key; the debounced term is appended automatically. */
  key: readonly unknown[];
  /** Runs only when the min-chars gate is open. Should be access-scoped server-side. */
  fetcher: (q: string) => Promise<T[]>;
  /** Minimum characters before hitting the server. Default 2. */
  minChars?: number;
  /** Debounce window in ms. Default 300. */
  debounceMs?: number;
}

export interface ServerSearchResult<T> {
  /** True once the debounced query passes the min-chars gate. */
  isSearching: boolean;
  /** Server matches (empty until searching). */
  results: T[];
  /** True while a search request is in flight. */
  loading: boolean;
  /** The debounced, trimmed query actually sent to the server. */
  debounced: string;
}

export function useServerSearch<T>(query: string, opts: ServerSearchOptions<T>): ServerSearchResult<T> {
  const debounced = useDebounce(query.trim(), opts.debounceMs ?? 300);
  const isSearching = debounced.length >= (opts.minChars ?? 2);

  const { data = [], isFetching } = useQuery({
    queryKey: [...opts.key, debounced],
    queryFn: () => opts.fetcher(debounced),
    enabled: isSearching,
  });

  return {
    isSearching,
    results: isSearching ? (data as T[]) : [],
    loading: isFetching && isSearching,
    debounced,
  };
}
